import React, { useState, useEffect } from "react";
import { scrollToTop } from "../utils/scrollUtils";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import UploadIcon from "@mui/icons-material/Upload";
import WarningIcon from "@mui/icons-material/Warning";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PreviewIcon from "@mui/icons-material/Preview";
import ArrowRight from "@mui/icons-material/ArrowRight";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HealthcareIcon from "@mui/icons-material/LocalHospital";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import FeedbackIcon from "@mui/icons-material/Feedback";
import InfoIcon from "@mui/icons-material/Info";
import DevicesIcon from "@mui/icons-material/Devices";
import Flashcard from "../components/Flashcard";
import FlowDiagram from "../components/FlowDiagram";
import CourseModule from "../components/CourseModule";
import SubModule from "../components/SubModule";
import Infographic from "../components/Infographic";
import FallRiskFactorsGuide from "../components/FallRiskFactorsGuide";
import {
  analyzeVideoWithGemini,
  mockAnalyzeVideo,
} from "../services/geminiService";
import { getCompletedLessons } from "../services/lessonService";
import CaseStudyGenerator from "../components/cases/caseStudyGenerator";

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

// Function to generate random choking scenarios for the Video Evaluation 2 tool
const generateRandomScenario = () => {
  // Arrays of scenario components for randomization
  const patients = [
    { age: 72, gender: "woman", condition: "mild dementia and dysphagia" },
    { age: 85, gender: "man", condition: "Parkinson's disease" },
    { age: 68, gender: "woman", condition: "post-stroke swallowing difficulties" },
    { age: 79, gender: "man", condition: "COPD and reduced cough strength" },
    { age: 81, gender: "woman", condition: "denture wearer with poor fitting dentures" },
    { age: 76, gender: "man", condition: "diabetes and peripheral neuropathy" },
    { age: 83, gender: "woman", condition: "mild cognitive impairment" },
    { age: 70, gender: "man", condition: "esophageal stricture" },
  ];

  const situations = [
    "eating a piece of meat",
    "consuming a meal too quickly",
    "taking medication with insufficient water",
    "laughing while eating a snack",
    "eating without proper dentures in place",
    "consuming food that wasn't cut into small enough pieces",
    "eating while lying down",
    "talking while eating",
  ];

  const statuses = [
    "showing universal choking sign with hands at throat",
    "unable to speak, cough, or breathe effectively",
    "making high-pitched sounds while inhaling",
    "displaying facial cyanosis (bluish discoloration)",
    "conscious but showing signs of panic and distress",
    "attempting to cough but producing weak, ineffective coughs",
    "showing signs of partial airway obstruction with labored breathing",
    "beginning to lose consciousness due to lack of oxygen",
  ];

  // Randomly select components
  const patient = patients[Math.floor(Math.random() * patients.length)];
  const situation = situations[Math.floor(Math.random() * situations.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];

  // Construct the scenario
  return `A ${patient.age}-year-old ${patient.gender} with ${patient.condition} is choking while ${situation}. The patient is ${status}. Demonstrate the proper emergency response protocol, including assessment, intervention techniques, and communication procedures.`;
};

function ChokingManagementCourse() {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [additionalCaseIncorrectAttempts, setAdditionalCaseIncorrectAttempts] =
    useState(0);
  const [firstCaseCompleted, setFirstCaseCompleted] = useState(false);
  const [activeCaseStudy, setActiveCaseStudy] = useState("case1");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showEvaluationNotification, setShowEvaluationNotification] =
    useState(false);
  const [correctlyAnsweredQuestions, setCorrectlyAnsweredQuestions] = useState(
    new Set()
  );
  const [expandedStep, setExpandedStep] = useState(null);
  const [activeSection, setActiveSection] = useState("learning");
  const [expandedModule, setExpandedModule] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(
    getCompletedLessons()
  );
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentLesson, setCurrentLesson] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [scenarioText, setScenarioText] = useState(generateRandomScenario());

  // Scenario is only generated once when the component mounts
  // and will only refresh when the page is refreshed
  const [stream, setStream] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [isAiGenerated, setIsAiGenerated] = useState(true); // Track if the analysis is AI-generated or mock
  const [activeModule, setActiveModule] = useState(null);
  const [moduleProgress, setModuleProgress] = useState({});
  const [isModuleStarted, setIsModuleStarted] = useState(false);
  const videoRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const MAX_ATTEMPTS = 3;

  const additionalFlashcards = [
    {
      question: "What medical conditions increase Mr. Miller's fall risk?",
      options: [
        "Vision problems and arthritis",
        "Diabetes and hypertension",
        "Heart disease and asthma",
        "Osteoporosis and dementia",
      ],
      correctAnswer: 1,
      explanation:
        "Mr. Miller's Type 2 Diabetes can affect balance and sensation, while hypertension can cause dizziness, both increasing fall risk.",
      relatedStep: 0,
    },
    {
      question:
        "What immediate action should be taken when assisting Mr. Miller?",
      options: [
        "Call for emergency assistance",
        "Assess his pain level",
        "Check surgical site",
        "Review medication schedule",
      ],
      correctAnswer: 1,
      explanation:
        "Pain assessment is crucial as it can affect mobility and increase fall risk post-surgery.",
      relatedStep: 1,
    },
    {
      question: "How should Mr. Miller's mobility be supported?",
      options: [
        "Encourage independent movement",
        "Use a gait belt and walker",
        "Provide wheelchair only",
        "Strict bed rest",
      ],
      correctAnswer: 1,
      explanation:
        "A gait belt and walker provide necessary support while promoting safe mobility during recovery.",
      relatedStep: 2,
    },
    {
      question:
        "What environmental modification is most important for Mr. Miller?",
      options: [
        "Remove all furniture",
        "Install grab bars",
        "Clear walking paths",
        "Change floor material",
      ],
      correctAnswer: 2,
      explanation:
        "Clear paths are essential for safe walker use and reducing fall risks during recovery.",
      relatedStep: 3,
    },
    {
      question: "How often should Mr. Miller's progress be monitored?",
      options: ["Daily", "Weekly", "Every 4 hours", "After each activity"],
      correctAnswer: 3,
      explanation:
        "Post-surgical recovery requires frequent monitoring, especially after physical activities.",
      relatedStep: 4,
    },
  ];

  const flashcards = [
    {
      question:
        "What are the key immediate actions to take after finding Mrs. Vance on the floor?",
      options: [
        "Move her to bed, check vital signs, call family",
        "Do not move her, call for assistance, check ABCs",
        "Help her stand up, give water, document incident",
        "Call emergency services, start CPR, notify supervisor",
      ],
      correctAnswer: 1,
      explanation:
        "It's crucial not to move a fallen patient until properly assessed. Call for assistance and check ABCs (Airway, Breathing, Circulation) first.",
      relatedStep: 0,
    },
    {
      question: "Which assessment should be prioritized first for Mrs. Vance?",
      options: [
        "Detailed medication review",
        "Musculoskeletal examination",
        "Consciousness and vital signs",
        "Environmental hazard assessment",
      ],
      correctAnswer: 2,
      explanation:
        "Initial assessment must focus on consciousness and vital signs to ensure patient stability before proceeding with other examinations.",
      relatedStep: 1,
    },
    {
      question:
        "What specific aspects need focus during Mrs. Vance's comprehensive evaluation?",
      options: [
        "Only cognitive function due to mild impairment",
        "Just blood pressure due to hypertension",
        "Cardiac, neurological, and musculoskeletal assessment",
        "Only check for visible injuries",
      ],
      correctAnswer: 2,
      explanation:
        "Given her conditions (hypertension, cognitive impairment, osteoarthritis), a thorough assessment of all these systems is crucial.",
      relatedStep: 2,
    },
    {
      question: "Who needs to be notified about Mrs. Vance's fall?",
      options: [
        "Only the supervisor",
        "Family and supervisor only",
        "Physician, family, and OT/PT specialists",
        "Just the emergency services",
      ],
      correctAnswer: 2,
      explanation:
        "All relevant parties need to be notified to ensure proper care coordination, especially given her multiple conditions.",
      relatedStep: 3,
    },
    {
      question: "How should Mrs. Vance's vital signs be monitored?",
      options: [
        "Once daily for a week",
        "Every 4 hours for 24 hours",
        "Hourly for 4 hours, then every 4 hours for 24 hours",
        "Only when symptoms appear",
      ],
      correctAnswer: 2,
      explanation:
        "Close monitoring is crucial in the first 4 hours, followed by regular checks for the next 24 hours, especially due to her hypertension.",
      relatedStep: 4,
    },
  ];

  const [showAdditionalCaseStudy, setShowAdditionalCaseStudy] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);

  // Watch for error threshold
  React.useEffect(() => {
    if (incorrectAttempts >= MAX_ATTEMPTS && !showAdditionalCaseStudy) {
      setShowErrorNotification(true);
      setShowAdditionalCaseStudy(true);
      // Reset after showing notification
      setTimeout(() => setShowErrorNotification(false), 5000);
    }
  }, [incorrectAttempts]);

  const caseStudy = {
    title: "Fall Risk Assessment Case: Mrs. Eleanor Vance",
    patientInfo: {
      name: "Mrs. Eleanor Vance",
      age: 82,
      position: "Sitting on the edge of her bed attempting to get up",
      conditions: [
        "Osteoarthritis (knees and hips)",
        "Mild cognitive impairment",
        "Hypertension (managed with medication)",
        "Multiple medications including diuretics",
      ],
    },
    objectives: [
      "Demonstrate proper immediate response protocol",
      "Conduct comprehensive assessment considering multiple conditions",
      "Implement appropriate monitoring schedule",
      "Coordinate communication with healthcare team and family",
      "Document all interventions and assessments accurately",
    ],
  };

  const additionalCaseStudy = {
    title: "Fall Risk Assessment Case: Mr. David Miller",
    patientInfo: {
      name: "Mr. David Miller",
      age: 36,
      position: "Standing in his kitchen, preparing breakfast",
      conditions: [
        "Type 2 Diabetes (managed with oral medication)",
        "Mild Hypertension (managed with lifestyle changes)",
        "Generalized Anxiety Disorder (managed with therapy and PRN medication)",
        "History of mild lower back pain",
      ],
    },
    objectives: [
      "Demonstrate proper immediate response protocol",
      "Conduct comprehensive assessment considering multiple conditions",
      "Implement appropriate monitoring schedule",
      "Coordinate communication with healthcare team and family",
      "Document all interventions and assessments accurately",
    ],
  };

  const additionalProtocolSteps = [
    {
      id: 0,
      title: "Medical Risk Assessment",
      description:
        "Identify and evaluate fall risk factors related to medical conditions.",
      content: [
        "Evaluate impact of diabetes on balance and sensation",
        "Assess blood pressure and its effects on dizziness",
        "Review anxiety levels and their impact on stability",
      ],
    },
    {
      id: 1,
      title: "Pain Management",
      description:
        "Monitor and manage lower back pain to ensure safe mobility.",
      content: [
        "Assess back pain levels during daily activities",
        "Review pain management techniques",
        "Monitor for pain-related mobility limitations",
      ],
    },
    {
      id: 2,
      title: "Mobility Support",
      description: "Provide appropriate guidance for safe movement.",
      content: [
        "Teach proper body mechanics for daily activities",
        "Demonstrate safe kitchen navigation techniques",
        "Guide proper posture during standing activities",
      ],
    },
    {
      id: 3,
      title: "Environment Safety",
      description:
        "Ensure kitchen and living areas are safe for daily activities.",
      content: [
        "Organize kitchen for easy access to frequently used items",
        "Remove potential trip hazards",
        "Ensure adequate lighting in all areas",
      ],
    },
    {
      id: 4,
      title: "Progress Monitoring",
      description:
        "Regular assessment of condition management and safety practices.",
      content: [
        "Monitor blood sugar and blood pressure readings",
        "Track anxiety levels and their management",
        "Document effectiveness of back pain management",
      ],
    },
  ];

  const originalProtocolSteps = [
    {
      title: "Immediate Response",
      description:
        "Do not move patient, assess consciousness and ABCs (Airway, Breathing, Circulation), call for assistance",
    },
    {
      title: "Initial Assessment",
      description:
        "Check vital signs, assess pain levels, and evaluate for any obvious injuries",
    },
    {
      title: "Comprehensive Evaluation",
      description:
        "Conduct thorough cardiac, neurological, and musculoskeletal assessment, focusing on ankle and lower back pain",
    },
    {
      title: "Notifications",
      description:
        "Alert physician, notify family (wife), request OT/PT evaluation",
    },
    {
      title: "Monitoring Protocol",
      description:
        "Monitor vital signs every 4 hours for 24 hours, conduct regular neurological checks",
    },
    {
      title: "Pain Management",
      description:
        "Offer appropriate pain relief, monitor effectiveness, document pain levels",
    },
    {
      title: "Follow-up Plan",
      description:
        "Arrange x-ray of right ankle, implement treatment plan based on findings, explore contributing factors",
    },
  ];

  // Update the protocolSteps variable to use for evaluation
  const protocolSteps = [
    {
      title: "Initial Assessment & Recognition",
      description:
        "Quickly identify signs of choking, distinguish between partial and complete airway obstruction, and assess the patient's ability to speak, cough, or breathe.",
    },
    {
      title: "Immediate Intervention",
      description:
        "Apply appropriate techniques based on obstruction severity, including encouraging coughing for mild cases, and performing abdominal thrusts (Heimlich maneuver) or chest thrusts as needed.",
    },
    {
      title: "Positioning & Technique",
      description:
        "Demonstrate proper positioning behind the patient, correct hand placement for abdominal thrusts, and appropriate force and direction of thrusts to dislodge the obstruction.",
    },
    {
      title: "Communication & Emergency Response",
      description:
        "Call for assistance, clearly communicate the emergency to others, and prepare for potential escalation to CPR if the patient becomes unconscious.",
    },
    {
      title: "Post-Choking Assessment & Care",
      description:
        "Evaluate the patient after successful intervention, monitor for complications, document the incident, and arrange for medical evaluation even if the obstruction is cleared.",
    },
  ];

  // Define submodules for each main module
  const introductionSubmodules = [
    {
      title: "Understanding Risks",
      description:
        "Learn about the various factors that contribute to fall risks in healthcare settings.",
      lessons: [
        {
          id: "intro-1",
          title: "Understanding Fall Risks",
          description:
            "Learn about the various factors that contribute to fall risks in healthcare settings.",
          duration: 10,
          type: "Video Lesson",
          component: FallRiskFactorsGuide,
          content: `Understanding Fall Risks in Healthcare`,
        },
        {
          id: "intro-2",
          title: "Impact of Falls",
          description:
            "Explore the physical, psychological, and financial impacts of falls.",
          duration: 10,
          type: "Interactive",
          content: `Impact of Falls in Healthcare Settings

          1. Physical Consequences:
          • Immediate Injuries
            - Fractures (especially hip fractures which occur in 5-10% of falls)
            - Head injuries and traumatic brain injury (TBI affects 32% of fall victims over 70)
            - Lacerations requiring sutures (present in 12-15% of documented falls)
            - Soft tissue injuries including bruising and hematomas (occur in 30-50% of falls)
          • Long-term Effects
            - Reduced mobility (40% never regain pre-fall mobility levels)
            - Chronic pain (affects 25-35% of fall injury survivors)
            - Permanent disability (10-15% of falls result in lasting functional impairment)
            - Increased risk of future falls (2-3 times higher after initial fall)

          2. Psychological Impact:
          • Post-Fall Syndrome
            - Fear of falling (affects 40-73% of those who have fallen previously)
            - Anxiety about movement and daily activities (reported by 65% of fall survivors)
            - Loss of confidence in balance abilities (leads to self-imposed restrictions)
            - Development of catastrophic thinking patterns about mobility
          • Depression and Mental Health
            - Clinical depression rates increase by 35% after serious falls
            - Social withdrawal due to embarrassment or fear (affects 50% of fall survivors)
            - Increased feelings of helplessness and dependency
            - Cognitive decline acceleration in older adults after falls

          3. Healthcare System Impact:
          • Clinical Resource Utilization
            - Emergency department visits (2.8 million annually due to falls in older adults)
            - Hospitalization rates (800,000+ hospitalizations annually from fall injuries)
            - Rehabilitation services (average 3-6 weeks for hip fracture recovery)
            - Extended care facility admissions (22% of fall patients require facility care)
          • Financial Burden
            - Average hospital cost per fall: $30,000-$50,000
            - Annual U.S. healthcare costs: $50+ billion for non-fatal falls
            - Long-term care costs: $75,000-$100,000 annually for nursing home care
            - Indirect costs: $25+ billion in lost productivity and caregiver time

          4. Quality of Life and Social Impact:
          • Independence and Living Situation
            - 40% of nursing home admissions occur after a fall
            - 25% of fallers reduce their daily activities
            - 70% report some level of activity restriction after falling
            - 60% experience decreased participation in social activities
          • Caregiver Impact
            - Family members provide average of 20+ hours weekly care after serious falls
            - Caregiver stress and burnout rates increase by 45-60%
            - Financial strain from reduced work hours or leaving employment
            - Relationship dynamics fundamentally altered by new care responsibilities

          5. Mortality Risk:
          • Fatal Fall Statistics
            - Falls are the leading cause of injury-related death in adults over 65
            - 32,000+ older adults die annually from fall complications
            - Mortality risk increases 4-fold in the year following a hip fracture
            - Men have a 33% higher fall-related mortality rate than women`,
        },
      ],
    },
    {
      title: "Risk Assessment",
      description:
        "Identify and understand populations at higher risk for falls.",
      lessons: [
        {
          id: "1.3",
          title: "High-Risk Populations",
          description:
            "Identify and understand populations at higher risk for falls.",
          duration: 10,
          type: "Case Study",
          content: `High-Risk Populations for Falls

          1. Elderly Individuals (65+):
          • Risk Factors
            - Decreased muscle strength
            - Balance problems
            - Slower reaction times
            - Multiple chronic conditions
          • Special Considerations
            - Regular strength training
            - Balance exercises
            - Environmental modifications

          2. Post-Operative Patients:
          • Immediate Risks
            - Effects of anesthesia
            - Pain medication
            - Weakness from bed rest
          • Monitoring Requirements
            - Frequent vital signs
            - Assistance with mobility
            - Pain management

          3. Neurological Conditions:
          • Common Conditions
            - Parkinson's disease
            - Multiple sclerosis
            - Stroke
            - Dementia
          • Specific Needs
            - Specialized mobility aids
            - Cognitive support
            - Environmental adaptation

          4. Medication-Related Risks:
          • High-Risk Medications
            - Psychotropics
            - Antihypertensives
            - Sedatives
            - Multiple medication use
          • Management Strategies
            - Regular medication review
            - Timing of medications
            - Alternative treatments

          5. Vision Impairment:
          • Adaptations Needed
            - Enhanced lighting
            - Contrast marking
            - Clear pathways
            - Regular vision checks`,
        },
      ],
    },
    {
      id: 2,
      title: "Assessment and Screening",
      description:
        "Learn comprehensive assessment techniques and screening tools for fall prevention.",
      icon: <PsychologyIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      duration: 45,
      infographicType: "assessment",
      lessons: [
        {
          id: "assessment-1",
          title: "Initial Assessment Components",
          description:
            "Master the key components of a thorough fall risk assessment.",
          duration: 15,
          type: "Video Lesson",
          content: `Comprehensive Fall Risk Assessment Components

          1. Physical Assessment:
          • Gait Evaluation
            - Walking pattern analysis
            - Step length and width
            - Walking speed
            - Turn stability
          • Balance Testing
            - Static balance
            - Dynamic balance
            - Postural control
            - Recovery reactions
          • Muscle Strength
            - Lower extremity strength
            - Core stability
            - Upper body strength
            - Joint flexibility

          2. Sensory Assessment:
          • Vision Check
            - Visual acuity
            - Depth perception
            - Peripheral vision
            - Color contrast sensitivity
          • Hearing Evaluation
            - Basic hearing test
            - Balance-related hearing issues
            - Environmental sound awareness

          3. Cognitive Screening:
          • Mental Status
            - Orientation assessment
            - Memory evaluation
            - Executive function
            - Attention span
          • Behavioral Assessment
            - Risk awareness
            - Safety compliance
            - Decision-making ability

          4. Environmental Review:
          • Home Safety
            - Lighting assessment
            - Floor surface evaluation
            - Furniture arrangement
            - Bathroom safety features
          • Assistive Device Check
            - Proper fit and condition
            - Usage technique
            - Maintenance needs`,
        },
        {
          id: "assessment-2",
          title: "Assessment Tools",
          description:
            "Learn to use standardized assessment tools effectively.",
          duration: 15,
          type: "Interactive",
          content: `Standardized Fall Risk Assessment Tools

          1. Morse Fall Scale (MFS):
          • Components and Scoring
            - History of falling (No = 0, Yes = 25 points)
            - Secondary diagnosis (No = 0, Yes = 15 points)
            - Ambulatory aid (None/bedrest/wheelchair = 0, Crutches/cane/walker = 15, Furniture = 30 points)
            - IV/Heparin lock (No = 0, Yes = 20 points)
            - Gait/Transferring (Normal/bedrest/immobile = 0, Weak = 10, Impaired = 20 points)
            - Mental status (Oriented = 0, Forgets limitations = 15 points)
          • Risk Stratification
            - 0-24: Low risk - Basic nursing care
            - 25-44: Moderate risk - Standard fall prevention interventions
            - 45+: High risk - High-risk fall prevention interventions
          • Validation Statistics
            - Sensitivity: 78% for predicting falls
            - Specificity: 83% when properly administered
            - Widely used in acute care and rehabilitation settings
            - Requires reassessment with any status change

          2. STRATIFY Risk Assessment Tool:
          • Five Key Risk Factors (1 point each)
            - Recent history of falls (within 3 months)
            - Agitation, confusion, or disorientation
            - Visual impairment affecting everyday function
            - Frequent toileting (urgency/nocturia/incontinence)
            - Transfer ability and mobility (requires assistance)
          • Clinical Application
            - Score ≥2 indicates high fall risk (sensitivity 93%, specificity 88%)
            - Developed specifically for hospital settings
            - Requires minimal training to administer (5-10 minutes)
            - Should be completed within 24 hours of admission
          • Implementation Protocol
            - Initial assessment on admission
            - Reassessment weekly or after condition changes
            - Documentation in electronic health record
            - Automatic triggers for prevention protocols

          3. Berg Balance Scale (BBS):
          • 14 Functional Tasks (0-4 points each)
            - Sitting to standing (without using hands)
            - Standing unsupported for 2 minutes
            - Sitting unsupported with feet on floor
            - Standing to sitting (controlled descent)
            - Transfers between surfaces
            - Standing with eyes closed
            - Standing with feet together
            - Reaching forward with outstretched arm
            - Retrieving object from floor
            - Turning to look behind
            - Turning 360 degrees
            - Placing alternate foot on stool
            - Standing with one foot in front
            - Standing on one leg
          • Clinical Interpretation
            - 0-20: High fall risk (wheelchair bound)
            - 21-40: Medium risk (walking with assistance)
            - 41-56: Low risk (independent)
          • Evidence Base
            - Gold standard for functional balance assessment
            - High test-retest reliability (ICC = 0.98)
            - Excellent predictor of falls in elderly (91% accuracy)
            - Takes 15-20 minutes to administer properly

          4. Timed Up and Go (TUG) Test:
          • Standardized Protocol
            - Patient seated in standard armchair (seat height 46 cm, arm height 65 cm)
            - On command, stand up, walk 3 meters at normal pace
            - Turn around at marked line
            - Return to chair and sit down completely
            - Timing starts on "go" command and stops when seated again
          • Equipment Requirements
            - Stopwatch (digital preferred for accuracy)
            - Measuring tape for precise 3-meter distance
            - Standard height chair without wheels
            - Walking aid if normally used (document type)
          • Clinical Interpretation
            - <10 seconds: Normal mobility, low fall risk
            - 10-14 seconds: Good mobility, some functional independence issues
            - 14-20 seconds: Moderate impairment, may need assistance
            - >20 seconds: Significant mobility impairment, high fall risk
            - >30 seconds: Severe impairment, dependent for most ADLs
          • Advantages
            - Quick (under 3 minutes to administer)
            - Requires minimal equipment
            - Highly predictive of fall risk (sensitivity 87%)
            - Correlates well with ADL function and community mobility`,
        },
        {
          id: "2.3",
          title: "Documentation Best Practices",
          description:
            "Master proper documentation techniques for fall risk assessments.",
          duration: 15,
          type: "Practice",
          content: `Documentation Guidelines for Fall Risk Assessment

          1. Patient History Documentation:
          • Fall History
            - Previous fall incidents
            - Circumstances of falls
            - Injury outcomes
            - Recovery process
          • Medical Background
            - Chronic conditions
            - Recent changes
            - Surgical history
            - Current symptoms

          2. Assessment Findings:
          • Physical Examination
            - Vital signs
            - Gait analysis results
            - Balance test scores
            - Strength measurements
          • Cognitive Status
            - Mental state findings
            - Behavioral observations
            - Communication ability
            - Safety awareness

          3. Risk Factors:
          • Environmental Hazards
            - Home safety evaluation
            - Lighting conditions
            - Floor surfaces
            - Bathroom setup
          • Personal Factors
            - Medication effects
            - Mobility status
            - Vision/hearing status
            - Daily routine risks

          4. Care Planning:
          • Interventions
            - Recommended modifications
            - Equipment needs
            - Safety measures
            - Education plans
          • Follow-up Schedule
            - Reassessment timing
            - Monitoring frequency
            - Progress evaluation
            - Plan adjustments`,
        },
      ],
    },
    {
      id: 3,
      title: "Prevention Strategies",
      description:
        "Learn effective strategies to prevent falls in various healthcare settings.",
      icon: <LocalHospitalIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      duration: 60,
      infographicType: "prevention",
      lessons: [
        {
          id: "3.1",
          title: "Environmental Modifications",
          description: "Learn to create safer environments to prevent falls.",
          duration: 20,
          type: "Video Lesson",
          content: `Environmental Safety Modifications

          1. Bedroom Safety:
          • Lighting
            - Night lights installation
            - Easy-to-reach light switches
            - Motion sensor lights
            - Adequate room illumination
          • Furniture Arrangement
            - Clear pathways to bathroom
            - Stable bed height (20-23 inches)
            - Secure bedside table
            - Sturdy chairs with armrests

          2. Bathroom Modifications:
          • Safety Features
            - Grab bars installation
            - Raised toilet seats
            - Non-slip shower mats
            - Shower chairs/benches
          • Accessibility
            - Clear entry/exit paths
            - Easy-reach toiletries
            - Adequate turning space
            - Emergency call system

          3. Common Areas:
          • Floor Safety
            - Non-slip flooring
            - Secured carpet edges
            - Contrast marking on steps
            - Remove throw rugs
          • Support Features
            - Handrails in hallways
            - Adequate seating areas
            - Storage at accessible height
            - Good ventilation

          4. Mobility Aid Setup:
          • Walker/Cane Placement
            - Proper height adjustment
            - Easy access storage
            - Clear usage space
            - Regular maintenance
          • Wheelchair Considerations
            - Wide doorways
            - Ramps where needed
            - Turn-around space
            - Brake maintenance`,
        },
        {
          id: "3.2",
          title: "Patient Education",
          description:
            "Master techniques for educating patients about fall prevention.",
          duration: 20,
          type: "Role Play",
          content: `Patient Education Strategies

          1. Safety Awareness Training:
          • Personal Risk Factors
            - Understanding medical conditions
            - Medication effects awareness
            - Recognizing fatigue signs
            - Identifying hazardous situations
          • Environmental Awareness
            - Home safety assessment
            - Hazard identification
            - Proper lighting use
            - Weather considerations

          2. Safe Movement Techniques:
          • Transfer Training
            - Bed to chair transitions
            - Toilet transfers
            - Car entry/exit
            - Standing from sitting
          • Walking Safety
            - Proper footwear use
            - Pace management
            - Turn techniques
            - Rest periods

          3. Emergency Response:
          • Call System Usage
            - When to call for help
            - How to use call buttons
            - Emergency numbers
            - Response expectations
          • Fall Recovery
            - What to do if you fall
            - How to get up safely
            - When to stay down
            - Calling for assistance

          4. Lifestyle Adaptations:
          • Daily Routine Modifications
            - Activity pacing
            - Rest scheduling
            - Medication timing
            - Hydration habits
          • Exercise Program
            - Balance exercises
            - Strength training
            - Flexibility work
            - Activity progression`,
        },
        {
          id: "assessment-3",
          title: "Monitoring and Follow-up",
          description: "Learn effective monitoring and follow-up strategies.",
          duration: 20,
          type: "Interactive",
          content: `Monitoring and Follow-up Protocols

          1. Regular Assessment Schedule:
          • Daily Monitoring
            - Medication compliance
            - Physical status changes
            - Mental status changes
            - Environmental checks
          • Weekly Reviews
            - Progress evaluation
            - Exercise compliance
            - Safety measure effectiveness
            - New risk factors

          2. Documentation Requirements:
          • Progress Notes
            - Changes in condition
            - New symptoms
            - Intervention effectiveness
            - Patient compliance
          • Incident Reports
            - Near-miss events
            - Actual falls
            - Contributing factors
            - Outcomes

          3. Care Plan Updates:
          • Intervention Adjustments
            - Modified exercises
            - Equipment changes
            - Schedule modifications
            - New safety measures
          • Team Communication
            - Care team updates
            - Family notifications
            - Provider reports
            - Service coordination

          4. Quality Improvement:
          • Outcome Tracking
            - Fall rate monitoring
            - Injury prevention
            - Program effectiveness
            - Patient satisfaction
          • Program Adjustments
            - Protocol updates
            - Staff training needs
            - Resource allocation
            - Best practice implementation`,
        },
      ],
    },
    {
      id: "monitoring",
      title: "Response and Monitoring",
      description:
        "Learn proper response protocols and ongoing monitoring techniques.",
      icon: <GroupsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
      duration: 45,
      infographicType: "monitoring",
      lessons: [
        {
          id: "monitoring-1",
          title: "Monitoring Protocols",
          description:
            "Implement effective monitoring systems for fall prevention.",
          duration: 15,
          type: "Video Lesson",
          content: `Comprehensive Monitoring Protocols for Fall Prevention

          1. Regular Observation Schedules:
          • Hourly Rounding
            - Purposeful checks focused on fall risks
            - 4 P's assessment: Pain, Position, Personal needs, Possessions
            - Documentation of patient status
            - Environmental safety verification
          • High-Risk Patient Monitoring
            - 15-30 minute checks for highest risk patients
            - Bed/chair alarm verification
            - Mobility assistance planning
            - Medication effect monitoring

          2. Technology-Assisted Monitoring:
          • Bed/Chair Alarm Systems
            - Proper sensitivity settings
            - Immediate response protocols
            - Regular functionality testing
            - Integration with nurse call systems
          • Video Monitoring
            - Privacy-respecting observation
            - Remote patient monitoring
            - Fall detection capabilities
            - Staff notification systems

          3. Activity Level Tracking:
          • Mobility Assessment
            - Daily activity logging
            - Assistance level documentation
            - Progress tracking
            - Activity tolerance evaluation
          • Exercise Compliance
            - Scheduled movement activities
            - Range of motion exercises
            - Balance training participation
            - Strength building progress

          4. Interdisciplinary Communication:
          • Shift Handoff Procedures
            - Fall risk status updates
            - Recent changes in condition
            - Intervention effectiveness
            - Special monitoring needs
          • Care Team Coordination
            - Physical therapy reports
            - Medication reviews
            - Nutritional status updates
            - Cognitive assessment findings`,
        },
        {
          id: "monitoring-2",
          title: "Emergency Response",
          description: "Learn proper emergency response procedures for falls.",
          duration: 15,
          type: "Simulation",
          content: `Emergency Response Procedures for Falls

          1. Immediate Assessment Protocol:
          • Primary Survey
            - Airway assessment
            - Breathing evaluation
            - Circulation check
            - Disability/neurological status
            - Exposure/environmental assessment
          • Injury Evaluation
            - Head injury screening
            - Fracture assessment
            - Soft tissue injury check
            - Pain evaluation
            - Vital signs monitoring

          2. Response Team Activation:
          • Code Response
            - When to call emergency codes
            - Team member responsibilities
            - Equipment requirements
            - Documentation during emergency
          • Medical Provider Notification
            - Information to communicate
            - Timing of notifications
            - Transfer of care procedures
            - Follow-up requirements

          3. Post-Fall Procedures:
          • Immediate Documentation
            - Fall circumstances
            - Patient condition
            - Interventions provided
            - Witness statements
            - Environmental factors
          • Family Communication
            - Notification timing
            - Information disclosure
            - Answering questions
            - Involving family in care planning
          
          4. Recovery Monitoring:
          • Post-Fall Assessment
            - 24-hour monitoring schedule
            - Delayed injury detection
            - Psychological impact evaluation
            - Mobility reassessment
          • Care Plan Modification
            - Additional safety measures
            - Increased monitoring
            - Equipment needs
            - Rehabilitation referrals`,
        },
        {
          id: "monitoring-3",
          title: "Quality Improvement",
          description:
            "Implement continuous improvement in fall prevention programs.",
          duration: 15,
          type: "Project",
          content: `Quality Improvement in Fall Prevention Programs

          1. Data Collection and Analysis:
          • Fall Incident Metrics
            - Fall rates per 1000 patient days
            - Injury severity classification
            - Time and location patterns
            - Contributing factors analysis
          • Prevention Effectiveness
            - Intervention compliance rates
            - Pre/post implementation comparisons
            - Cost-benefit analysis
            - Staff satisfaction surveys

          2. Root Cause Analysis:
          • Investigation Process
            - Timeline reconstruction
            - Contributing factor identification
            - System failure analysis
            - Human factors evaluation
          • Pattern Recognition
            - Common scenarios identification
            - High-risk times/locations
            - Staffing impact assessment
            - Equipment failure trends

          3. Improvement Implementation:
          • PDSA Cycles
            - Plan: Identifying improvement opportunities
            - Do: Implementing targeted changes
            - Study: Measuring outcomes
            - Act: Standardizing effective changes
          • Staff Engagement
            - Fall prevention champions
            - Frontline staff input
            - Interdisciplinary collaboration
            - Recognition programs

          4. Sustainability Planning:
          • Ongoing Education
            - Regular staff training
            - New evidence integration
            - Competency verification
            - Just-in-time learning
          • Policy Development
            - Protocol updates
            - Best practice alignment
            - Regulatory compliance
            - Standardization across units`,
        },
      ],
    },
  ];

  const handleModuleChange = (moduleIndex) => {
    setExpandedModule(expandedModule === moduleIndex ? -1 : moduleIndex);
  };

  const handleStepClick = (stepId) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  // Reset expanded step when switching case studies
  React.useEffect(() => {
    setExpandedStep(null);
  }, [activeCaseStudy]);

  const handleLessonComplete = (lessonId) => {
    // This function is kept for compatibility but is no longer needed
    // as lesson completion is now handled by the lessonService
    // and the ModuleContentPage component
  };

  // Effect to update completedLessons when returning to this page
  useEffect(() => {
    // Update the completedLessons state from localStorage when the component mounts
    setCompletedLessons(getCompletedLessons());
  }, [activeSection]); // Re-run when activeSection changes (e.g., when returning to content tab)

  const handleStartLesson = (lesson) => {
    setCurrentLesson(lesson.id);
    navigate(`/module-content/${lesson.id}`);
    scrollToTop();
  };

  // Define the remaining submodule arrays
  const assessmentSubmodules = [
    {
      title: "Risk Assessment Tools",
      description: "Learn about different tools used to assess fall risks.",
      lessons: [
        {
          id: "assessment-1",
          title: "Assessment Tools Overview",
          description: "Overview of common fall risk assessment tools.",
          duration: 15,
          type: "Interactive Guide",
          content: `Fall Risk Assessment Tools Overview

          1. Morse Fall Scale
          • Key Components
            - History of falling
            - Secondary diagnosis
            - Ambulatory aid
            - IV/saline lock
            - Gait/transferring
            - Mental status
          • Scoring
            - 0-24: Low risk
            - 25-44: Moderate risk
            - 45+: High risk

          2. STRATIFY Tool
          • Assessment Areas
            - Recent history of falls
            - Agitation
            - Visual impairment
            - Frequent toileting
            - Transfer/mobility score
          • Application
            - Primarily used in acute care settings
            - Quick administration (under 5 minutes)
            - High sensitivity in elderly populations`,
        },
        {
          id: "assessment-2",
          title: "Comprehensive Assessment",
          description:
            "Learn how to conduct a comprehensive fall risk assessment.",
          duration: 20,
          type: "Procedure Guide",
          content: `Comprehensive Fall Risk Assessment

          1. Medical History Review
          • Key Elements
            - Previous falls (circumstances, injuries)
            - Chronic conditions (diabetes, arthritis, etc.)
            - Medication review (polypharmacy, high-risk medications)
            - Sensory deficits (vision, hearing, proprioception)
          • Documentation
            - Standardized format
            - Temporal relationships
            - Severity indicators
            - Treatment responses

          2. Physical Examination
          • Balance Assessment
            - Timed Up and Go Test (TUG)
            - Berg Balance Scale
            - Single Leg Stance
            - Functional Reach Test
          • Gait Evaluation
            - Walking speed
            - Step length and height
            - Base of support
            - Path deviation`,
        },
      ],
    },
    {
      title: "Evaluation Methods",
      description:
        "Learn how to evaluate the effectiveness of fall prevention strategies.",
      lessons: [
        {
          id: "assessment-3",
          title: "Evaluation Methods",
          description:
            "Learn how to evaluate the effectiveness of fall prevention strategies.",
          duration: 15,
          type: "Interactive Guide",
          content: `Evaluation Methods for Fall Prevention Strategies

          1. Outcome Measures
          • Primary Metrics
            - Fall incidence rates
            - Injury severity classification
            - Near-miss reporting
            - Time between falls
          • Secondary Indicators
            - Mobility improvements
            - Balance test scores
            - Fear of falling scales
            - Quality of life measures

          2. Program Evaluation
          • Process Measures
            - Staff compliance with protocols
            - Assessment completion rates
            - Intervention implementation timing
            - Documentation quality
          • System Analysis
            - Environmental modification completion
            - Staff training effectiveness
            - Resource allocation efficiency
            - Interdisciplinary communication`,
        },
      ],
    },
  ];

  const interventionSubmodules = [
    {
      title: "Clinical Interventions",
      description: "Learn about clinical interventions to prevent falls.",
      lessons: [
        {
          id: "intervention-1",
          title: "Medication Management",
          description:
            "Learn about medication management strategies to reduce fall risks.",
          duration: 15,
          type: "Interactive Guide",
          content: `Medication Management for Fall Prevention

          1. High-Risk Medications
          • Psychotropics
            - Benzodiazepines
            - Antipsychotics
            - Sedative-hypnotics
            - Antidepressants
          • Cardiovascular Agents
            - Antihypertensives
            - Diuretics
            - Beta-blockers
            - Vasodilators

          2. Medication Review Process
          • Regular Schedule
            - Upon admission
            - After falls
            - With new prescriptions
            - Quarterly for long-term care
          • Key Considerations
            - Necessity of each medication
            - Potential alternatives
            - Dose reduction possibilities
            - Timing optimization`,
        },
        {
          id: "intervention-2",
          title: "Exercise Programs",
          description:
            "Design and implement effective exercise programs for fall prevention.",
          duration: 20,
          type: "Procedure Guide",
          content: `Exercise Programs for Fall Prevention

          1. Core Components
          • Balance Training
            - Static balance exercises
            - Dynamic balance activities
            - Perturbation training
            - Proprioceptive exercises
          • Strength Building
            - Lower extremity focus
            - Core strengthening
            - Progressive resistance
            - Functional movements

          2. Program Design
          • Frequency and Duration
            - 2-3 times weekly minimum
            - 30-60 minute sessions
            - 12+ weeks for optimal results
            - Ongoing maintenance
          • Progression Principles
            - Begin with seated exercises if needed
            - Advance to standing with support
            - Progress to unsupported standing
            - Incorporate dynamic movements`,
        },
      ],
    },
    {
      title: "Multifactorial Approaches",
      description: "Learn about comprehensive approaches to fall prevention.",
      lessons: [
        {
          id: "intervention-3",
          title: "Multifactorial Approaches",
          description: "Implement comprehensive fall prevention strategies.",
          duration: 20,
          type: "Case Study",
          content: `Multifactorial Approaches to Fall Prevention

          1. Comprehensive Assessment
          • Medical Evaluation
            - Chronic condition management
            - Medication review and adjustment
            - Vision and hearing assessment
            - Neurological examination
          • Functional Assessment
            - Gait and balance evaluation
            - Muscle strength testing
            - Transfer ability assessment
            - Activities of daily living review

          2. Personalized Interventions
          • Exercise Programs
            - Balance training
            - Strength building
            - Flexibility improvement
            - Endurance development
          • Environmental Modifications
            - Home safety assessment
            - Assistive device provision
            - Lighting improvements
            - Removal of hazards`,
        },
      ],
    },
  ];

  const monitoringSubmodules = [
    {
      title: "Emergency Response",
      description: "Learn proper emergency response procedures for falls.",
      lessons: [
        {
          id: "monitoring-1",
          title: "Immediate Response Protocol",
          description:
            "Learn the proper steps to take immediately after a fall occurs.",
          duration: 15,
          type: "Procedure Guide",
          content: `Immediate Response Protocol for Falls

          1. Initial Assessment
          • Primary Survey
            - Check responsiveness
            - Assess airway, breathing, circulation
            - Control any obvious bleeding
            - Check for signs of head injury
          • Secondary Survey
            - Assess for fractures or dislocations
            - Check for soft tissue injuries
            - Evaluate neurological status
            - Monitor vital signs

          2. Decision Making
          • Move vs. Don't Move
            - Suspected spinal injury: Do not move
            - Minor injury, safe environment: Assist to comfortable position
            - Serious injury: Maintain position, call emergency services
            - No injury: Assist to rise using proper technique`,
        },
        {
          id: "monitoring-2",
          title: "Post-Fall Assessment",
          description:
            "Conduct a thorough assessment after a fall has occurred.",
          duration: 15,
          type: "Interactive Guide",
          content: `Post-Fall Assessment Protocol

          1. Clinical Evaluation
          • Immediate Assessment
            - Vital signs monitoring
            - Pain assessment
            - Neurological checks
            - Injury documentation
          • Follow-up Evaluation
            - 24-hour monitoring schedule
            - Delayed symptom identification
            - Functional ability reassessment
            - Psychological impact evaluation

          2. Root Cause Analysis
          • Circumstantial Factors
            - Time and location
            - Activity during fall
            - Environmental conditions
            - Assistive devices used
          • Contributing Factors
            - Medication timing
            - Recent health changes
            - Staffing considerations
            - Equipment functionality`,
        },
      ],
    },
    {
      title: "Monitoring Protocols",
      description:
        "Learn about ongoing monitoring techniques for fall prevention.",
      lessons: [
        {
          id: "monitoring-3",
          title: "Ongoing Monitoring",
          description:
            "Implement continuous monitoring protocols for high-risk patients.",
          duration: 15,
          type: "Procedure Guide",
          content: `Ongoing Monitoring Protocols

          1. Regular Assessment Schedule
          • Daily Checks
            - Mobility status
            - Medication effects
            - Mental status changes
            - Environmental checks
          • Weekly Reviews
            - Progress evaluation
            - Exercise compliance
            - Safety measure effectiveness
            - New risk factors

          2. Documentation Requirements
          • Progress Notes
            - Changes in condition
            - New symptoms
            - Intervention effectiveness
            - Patient compliance
          • Incident Reports
            - Near-miss events
            - Actual falls
            - Contributing factors
            - Outcomes`,
        },
      ],
    },
  ];

  // Effect to handle error threshold
  React.useEffect(() => {
    if (incorrectAttempts >= MAX_ATTEMPTS && !showAdditionalCaseStudy) {
      setShowErrorNotification(true);
      setShowAdditionalCaseStudy(true);
      setTimeout(() => setShowErrorNotification(false), 5000);
    }
  }, [incorrectAttempts]);

  // Effect to handle case study completion
  React.useEffect(() => {
    if (firstCaseCompleted && showAdditionalCaseStudy) {
      setActiveCaseStudy("case2");
      setCompletedSteps([]);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);
    }
  }, [firstCaseCompleted, showAdditionalCaseStudy]);

  const [shouldResetIndex, setShouldResetIndex] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);

  const handleCorrectAnswer = (cardIndex) => {
    const currentCards =
      activeCaseStudy === "case1" ? flashcards : additionalFlashcards;
    const card = currentCards[cardIndex];

    // Create a prefixed index to separate case study 1 and 2 answers
    const prefixedIndex = `${activeCaseStudy}-${cardIndex}`;
    const newCorrectAnswers = new Set(correctlyAnsweredQuestions);
    newCorrectAnswers.add(prefixedIndex);
    setCorrectlyAnsweredQuestions(newCorrectAnswers);

    // Count correctly answered questions for the current case study
    const currentCaseAnswers = Array.from(newCorrectAnswers).filter((id) =>
      id.startsWith(activeCaseStudy)
    ).length;

    // Check completion for case studies
    if (activeCaseStudy === "case1" && currentCaseAnswers === 5) {
      setFirstCaseCompleted(true);

      // Handle course completion based on whether case study 2 is needed
      if (showAdditionalCaseStudy) {
        // Switch to case study 2 if needed
        setActiveCaseStudy("case2");
        setShouldResetIndex(true);
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
          setShouldResetIndex(false);
        }, 1500);
      } else {
        // Complete course if no case study 2 is needed
        setCourseCompleted(true);
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
          setActiveSection("video");
        }, 2000);
      }
    } else if (activeCaseStudy === "case2" && currentCaseAnswers === 5) {
      // Case study 2 completion
      setCourseCompleted(true);
      setShowSuccessNotification(true);
      setTimeout(() => {
        setShowSuccessNotification(false);
        setActiveSection("video");
      }, 2000);
    }

    // Update completed steps for protocol diagram
    if (card.relatedStep !== undefined) {
      const newCompletedSteps = [...completedSteps];
      if (!newCompletedSteps.includes(card.relatedStep)) {
        newCompletedSteps.push(card.relatedStep);
        setCompletedSteps(newCompletedSteps);
      }
    }
  };

  const handleIncorrectAnswer = () => {
    if (activeCaseStudy === "case1") {
      setIncorrectAttempts((prev) => Math.min(prev + 1, MAX_ATTEMPTS));
    } else {
      setAdditionalCaseIncorrectAttempts((prev) =>
        Math.min(prev + 1, MAX_ATTEMPTS)
      );
    }
  };

  const ErrorCrosses = () => {
    const attempts =
      activeCaseStudy === "case2"
        ? additionalCaseIncorrectAttempts
        : incorrectAttempts;

    return (
      <Box
        sx={{
          position: "absolute",
          top: -24,
          right: 24,
          display: "flex",
          gap: 1.5,
          padding: "4px 12px",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          boxShadow: 2,
          border: 1,
          borderColor: "divider",
        }}
      >
        {[...Array(MAX_ATTEMPTS)].map((_, index) => (
          <CloseIcon
            key={index}
            sx={{
              color: index < attempts ? "error.main" : "text.disabled",
              fontSize: "1.75rem",
              filter:
                index < attempts
                  ? "drop-shadow(0 0 2px rgba(211, 47, 47, 0.5))"
                  : "none",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </Box>
    );
  };

  const [isCaseStudyLoading, setIsCaseStudyLoading] = useState(false);
  const [currentCaseStudy, setCurrentCaseStudy] = useState(null);

  useEffect(() => {
    if (activeSection === "evaluation") {
      setIsCaseStudyLoading(true);
      // The CaseStudyGenerator component will handle the actual generation
      setCurrentCaseStudy(null);
      setIsCaseStudyLoading(false);
    }
  }, [activeSection]);

  const [randomModule, setRandomModule] = useState(null);

  // Add this effect to set the random module once when the component mounts
  useEffect(() => {
    const modules = [
      "initial-assessment",
      "immediate-response",
      "notification-and-communication",
      "post-choking-monitoring",
      "documentation-reporting",
      "preventive-measures"
    ];
    setRandomModule(modules[Math.floor(Math.random() * modules.length)]);
  }, []);

  return (
    <Container maxWidth="xl">
      {/* Course Header */}
      <Box sx={{ mb: 5, pt: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
            Choking Management Protocol
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, textAlign: 'center', mx: 'auto', mb: 3 }}>
            Essential steps to quickly identify and respond to choking incidents.
          </Typography>
        </Box>

        <Tabs
          value={activeSection}
          onChange={(e, newValue) => setActiveSection(newValue)}
          centered
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 500,
              minHeight: 48,
              py: 1,
            },
            '& .MuiTabs-indicator': {
              height: 3,
            }
          }}
        >
          <Tab
            icon={<SchoolIcon />}
            iconPosition="start"
            label="Learning Modules"
            value="learning"
          />
          <Tab
            icon={<AssignmentIcon />}
            iconPosition="start"
            label="Case-Study Evaluation"
            value="evaluation"
          />
          {/* Video Evaluation tab */}
          <Tab
            icon={<VideocamIcon />}
            iconPosition="start"
            label="Video Evaluation"
            value="video2"
          />
        </Tabs>
      </Box>

      {/* Course Content Section */}
      <TabPanel value={activeSection} index="content">
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* Main module tabs */}
          <Tabs
            value={expandedModule}
            onChange={(e, newValue) => handleModuleChange(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 4,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab
              icon={<SchoolIcon />}
              iconPosition="start"
              label="Introduction to Fall Prevention"
            />
            <Tab
              icon={<AssignmentIcon />}
              iconPosition="start"
              label="Assessment and Evaluation"
            />
            <Tab
              icon={<AccessibilityIcon />}
              iconPosition="start"
              label="Intervention Strategies"
            />
            <Tab
              icon={<MonitorHeartIcon />}
              iconPosition="start"
              label="Response and Monitoring"
            />
          </Tabs>

          {/* Submodules for the selected main module */}
          {expandedModule === 0 && (
            <SubModule
              sections={introductionSubmodules}
              onSectionClick={handleStartLesson}
            />
          )}
          {expandedModule === 1 && (
            <SubModule
              sections={assessmentSubmodules}
              onSectionClick={handleStartLesson}
            />
          )}
          {expandedModule === 2 && (
            <SubModule
              sections={interventionSubmodules}
              onSectionClick={handleStartLesson}
            />
          )}
          {expandedModule === 3 && (
            <SubModule
              sections={monitoringSubmodules}
              onSectionClick={handleStartLesson}
            />
          )}
        </Box>
      </TabPanel>

      {/* Learning Modules Section */}
      <TabPanel value={activeSection} index="learning">
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={3}>
            {/* Module 1 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <DoneIcon sx={{ fontSize: 40, color: "#7857cd", mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Initial Assessment and Recognition
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Learn to identify mild and severe airway obstructions,
                  assess patient responsiveness, and evaluate risk factors for
                  choking.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="30 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(229, 115, 115, 0.1)",
                      color: "#7857cd",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate("/modules/cm/initial-assessment"); // Update progress

                      // Update progress tracking
                      setActiveModule("patient-assessment");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["patient-assessment"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "patient-assessment": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#7857cd",
                      color: "#7857cd",
                      "&:hover": {
                        borderColor: "#5523d3",
                        backgroundColor: "rgba(229, 115, 115, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Module 2 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssignmentIcon
                    sx={{ fontSize: 40, color: "#64b5f6", mr: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Immediate Response
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Understand the appropriate interventions for partial and
                  complete airway obstructions, including the Heimlich
                  maneuver, chest thrusts, and CPR
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="25 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(100, 181, 246, 0.1)",
                      color: "#64b5f6",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate("/modules/cm/immediate-response"); // Update progress

                      // Update progress tracking
                      setActiveModule("medication-management");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["medication-management"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "medication-management": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#64b5f6",
                      color: "#64b5f6",
                      "&:hover": {
                        borderColor: "#1976d2",
                        backgroundColor: "rgba(100, 181, 246, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Module 3 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AccessibilityNewIcon
                    sx={{ fontSize: 40, color: "#81c784", mr: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Notification and Communication
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Master the steps to activate emergency response, notify the
                  medical team, and provide accurate patient handover.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="40 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(129, 199, 132, 0.1)",
                      color: "#81c784",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate("/modules/cm/notification-and-communication");

                      // Update progress tracking
                      setActiveModule("cognitive-impairment");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["cognitive-impairment"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "cognitive-impairment": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#81c784",
                      color: "#81c784",
                      "&:hover": {
                        borderColor: "#388e3c",
                        backgroundColor: "rgba(129, 199, 132, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Module 4 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocalHospitalIcon
                    sx={{ fontSize: 40, color: "#ba68c8", mr: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Post-Choking Monitoring and Reassessment
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Learn to monitor for complications post-resolution, assess
                  respiratory status, and ensure proper follow-up care.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="35 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(186, 104, 200, 0.1)",
                      color: "#ba68c8",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate("/modules/cm/post-monitoring-and-assessment");

                      // Update progress tracking
                      setActiveModule("family-education");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["family-education"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "family-education": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#ba68c8",
                      color: "#ba68c8",
                      "&:hover": {
                        borderColor: "#7b1fa2",
                        backgroundColor: "rgba(186, 104, 200, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Module 5 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AssignmentIcon
                    sx={{ fontSize: 40, color: "#64b5f6", mr: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Documentation, Reporting, and Analysis
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Develop skills to accurately document events, report
                  incidents, and analyze outcomes for quality improvement.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="35 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(186, 104, 200, 0.1)",
                      color: "#ba68c8",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate("/modules/cm/documentation-and-analysis");

                      // Update progress tracking
                      setActiveModule("family-education");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["family-education"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "family-education": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#ba68c8",
                      color: "#ba68c8",
                      "&:hover": {
                        borderColor: "#7b1fa2",
                        backgroundColor: "rgba(186, 104, 200, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Module 6 */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary.light",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <WarningIcon sx={{ fontSize: 40, color: "red", mr: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Preventive Measures and Patient Education
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Explore strategies to prevent choking through patient
                  education, environmental modifications, and dietary
                  adjustments.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Chip
                    label="35 min"
                    size="small"
                    icon={
                      <AccessibilityIcon
                        sx={{ fontSize: "0.8rem !important" }}
                      />
                    }
                    sx={{
                      bgcolor: "rgba(186, 104, 200, 0.1)",
                      color: "#ba68c8",
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Open in new window
                      navigate(
                        "/modules/cm/prevent-measures-and-patient-education"
                      );

                      // Update progress tracking
                      setActiveModule("family-education");
                      setIsModuleStarted(true);
                      // Initialize progress if not already set
                      if (!moduleProgress["family-education"]) {
                        setModuleProgress((prev) => ({
                          ...prev,
                          "family-education": {
                            started: true,
                            completed: false,
                            progress: 0,
                          },
                        }));
                      }
                    }}
                    sx={{
                      borderColor: "#ba68c8",
                      color: "#ba68c8",
                      "&:hover": {
                        borderColor: "#7b1fa2",
                        backgroundColor: "rgba(186, 104, 200, 0.1)",
                      },
                    }}
                  >
                    Start Module
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              p: 3,
              bgcolor: "rgba(25, 118, 210, 0.05)",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "primary.main",
                mb: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <InfoIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
              Learning Path Recommendation
            </Typography>
            <Typography variant="body2">
              Based on your progress, we recommend completing the{" "}
              <strong>Notification and Communication</strong> and{" "}
              <strong>Post-Choking Monitoring and Reassessment</strong>{" "}
              modules next. These will enhance your ability to identify fall
              risks in patients with various conditions and develop
              appropriate prevention strategies tailored to specific patient
              needs.
            </Typography>
          </Box>
        </Box>
      </TabPanel>

      {/* Evaluation Section */}
      <TabPanel value={activeSection} index="evaluation">
        <Box sx={{ width: "100%", mb: 3 }}>
          {isCaseStudyLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <CaseStudyGenerator
              courseName="Choking Management"
              module={randomModule}
              onComplete={() => setActiveSection("video2")}
            />
          )}
        </Box>
      </TabPanel>

      {/* Video Evaluation Section */}
      <TabPanel value={activeSection} index="video">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: "100%",
                backgroundColor: "#ffffff",
                borderRadius: 2,
                boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 2,
                  }}
                >
                  Upload Care Technique Video
                </Typography>

                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{ mb: 2, mt: 3 }}
                >
                  Caretaker Response Mock Drill
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <strong>Scenario:</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  A 72-year-old woman has fallen. She has underlying conditions
                  of memory loss and dizziness. One person will verbally mock
                  the patient's responses, while the caretaker will demonstrate
                  the response protocol.
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                Mock the drill based on the scenario and then record or upload a
                video of your fall response and protocol for AI-powered analysis
                and feedback.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <strong>Instructions:</strong>
                <ul
                  style={{
                    paddingLeft: "20px",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <li>
                    Choose either the "Record Video" or "Upload Video" option
                    below
                  </li>
                  <li>
                    For recording: Click "Start Recording", perform the care
                    technique, then click "Stop Recording"
                  </li>
                  <li>
                    For uploading: Select a video file (MP4 or MOV format, max
                    15MB)
                  </li>
                  <li>
                    Click "Analyze Video" to receive detailed feedback on your
                    technique
                  </li>
                  <li>
                    Ensure good lighting and clear visibility of all movements
                    during the demonstration
                  </li>
                </ul>
              </Typography>

              {/* Video Preview */}
              {(stream || recordedVideo) && (
                <Box sx={{ mb: 3, position: "relative" }}>
                  <video
                    ref={videoRef}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      backgroundColor: "#000",
                    }}
                    autoPlay
                    muted
                    playsInline
                  />
                </Box>
              )}

              {/* Upload and Record Options */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* File Upload Option */}
                <Grid item xs={12} sm={6}>
                  <Box
                    component="label"
                    htmlFor="video-upload"
                    sx={{
                      border: "2px dashed",
                      borderColor: "primary.main",
                      borderRadius: 2,
                      p: 3,
                      height: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <input
                      type="file"
                      id="video-upload"
                      accept="video/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Check if file size exceeds 15MB (15 * 1024 * 1024 bytes)
                          if (file.size > 15 * 1024 * 1024) {
                            setAnalysisError(
                              "File size exceeds 15MB limit. Please upload a smaller video file."
                            );
                            e.target.value = null; // Reset the input
                            return;
                          }

                          if (stream) {
                            stream.getTracks().forEach((track) => track.stop());
                            setStream(null);
                          }
                          setUploadedVideo(file);
                          setRecordedVideo(null);
                          setAnalysisComplete(false);
                          setAnalysisError(null); // Clear any previous errors
                        }
                      }}
                    />
                    <UploadIcon
                      sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                    />
                    <Typography>Upload Video</Typography>
                    <Typography
                      variant="body2"
                      color="error.main"
                      fontWeight="500"
                    >
                      MP4, MOV (max 15MB)
                    </Typography>
                  </Box>
                </Grid>

                {/* Camera Recording Option */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      border: "2px dashed",
                      borderColor: "primary.main",
                      borderRadius: 2,
                      p: 3,
                      height: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={async () => {
                      if (isRecording) {
                        // Stop recording
                        mediaRecorderRef.current?.stop();
                        setIsRecording(false);
                      } else if (stream) {
                        // Start recording
                        chunksRef.current = [];
                        const mediaRecorder = new MediaRecorder(stream);
                        mediaRecorderRef.current = mediaRecorder;
                        mediaRecorder.ondataavailable = (e) => {
                          if (e.data.size > 0) {
                            chunksRef.current.push(e.data);
                          }
                        };
                        mediaRecorder.onstop = () => {
                          const blob = new Blob(chunksRef.current, {
                            type: "video/mp4",
                          });
                          const videoFile = new File(
                            [blob],
                            "recorded-video.mp4",
                            { type: "video/mp4" }
                          );
                          setRecordedVideo(URL.createObjectURL(blob));
                          setUploadedVideo(videoFile);
                          setAnalysisComplete(false);
                          stream.getTracks().forEach((track) => track.stop());
                          setStream(null);
                        };
                        mediaRecorder.start();
                        setIsRecording(true);
                      } else {
                        // Start camera
                        try {
                          const mediaStream =
                            await navigator.mediaDevices.getUserMedia({
                              video: true,
                            });
                          setStream(mediaStream);
                          if (videoRef.current) {
                            videoRef.current.srcObject = mediaStream;
                          }
                          setUploadedVideo(null);
                          setRecordedVideo(null);
                        } catch (err) {
                          console.error("Error accessing camera:", err);
                        }
                      }
                    }}
                  >
                    {isRecording ? (
                      <>
                        <StopIcon
                          sx={{ fontSize: 40, color: "error.main", mb: 1 }}
                        />
                        <Typography color="error.main">
                          Stop Recording
                        </Typography>
                      </>
                    ) : (
                      <>
                        <VideocamIcon
                          sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                        />
                        <Typography>
                          {stream ? "Start Recording" : "Use Camera"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {uploadedVideo && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selected video: {uploadedVideo.name}
                  </Typography>
                  {/* Video Preview */}
                  <Box
                    sx={{ mt: 2, mb: 2, width: "100%", position: "relative" }}
                  >
                    <video
                      controls
                      width="100%"
                      height="auto"
                      src={
                        recordedVideo ||
                        (uploadedVideo
                          ? URL.createObjectURL(uploadedVideo)
                          : "")
                      }
                      style={{ borderRadius: "8px" }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={analyzing}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                      backgroundColor: "primary.main",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                    onClick={async () => {
                      const API_URL = "http://localhost:3001";
                      const MAX_RETRIES = 3;
                      let retryCount = 0;
                      try {
                        setAnalyzing(true);
                        setAnalysisError(null);

                        // Function to test backend connection
                        const testBackend = async () => {
                          try {
                            console.log("Testing backend connection...");
                            const testResponse = await fetch(
                              `${API_URL}/api/video/test`
                            );
                            const testData = await testResponse.json();
                            console.log("Backend test response:", testData);
                            return testResponse.ok;
                          } catch (error) {
                            console.error("Backend test failed:", error);
                            return false;
                          }
                        };

                        // Try to connect to backend with retries
                        while (retryCount < MAX_RETRIES) {
                          if (await testBackend()) {
                            break;
                          }
                          retryCount++;
                          if (retryCount === MAX_RETRIES) {
                            throw new Error(
                              "Could not connect to the analysis server after multiple attempts"
                            );
                          }
                          await new Promise((resolve) =>
                            setTimeout(resolve, 1000)
                          ); // Wait 1 second before retry
                        }
                        // Connection check is now handled by testBackend function

                        // Prepare form data
                        console.log("Preparing video for upload...");
                        const formData = new FormData();
                        formData.append("video", uploadedVideo);

                        // Log video details
                        console.log("Video details:", {
                          name: uploadedVideo.name,
                          type: uploadedVideo.type,
                          size: uploadedVideo.size,
                        });

                        console.log(
                          "Sending video for analysis:",
                          uploadedVideo
                        );

                        // Send video for analysis
                        console.log("Sending video to backend...");
                        const response = await fetch(
                          `${API_URL}/api/video/analyze`,
                          {
                            method: "POST",
                            body: formData,
                            headers: {
                              Accept: "application/json",
                            },
                            // Add these options for better error handling
                            mode: "cors",
                            credentials: "omit",
                          }
                        );

                        if (!response.ok) {
                          const errorData = await response.json();
                          throw new Error(
                            errorData.details ||
                              errorData.error ||
                              "Video analysis failed"
                          );
                        }

                        const data = await response.json();
                        if (data.success) {
                          setAnalysisResults(data.analysis);
                          setAnalysisComplete(true);
                        } else {
                          throw new Error(data.error || "Analysis failed");
                        }
                      } catch (error) {
                        console.error("Analysis error:", error);
                        let errorMessage = error.message;

                        // Provide more specific error messages
                        if (error.message === "Failed to fetch") {
                          errorMessage =
                            "Could not connect to the analysis server. Please ensure the server is running.";
                        } else if (error.message.includes("413")) {
                          errorMessage =
                            "Video file is too large. Please upload a smaller file (max 15MB).";
                        } else if (error.message.includes("415")) {
                          errorMessage =
                            "Unsupported video format. Please use MP4 or MOV format.";
                        }

                        setAnalysisError(errorMessage);
                        setAnalysisComplete(false);
                      } finally {
                        setAnalyzing(false);
                      }
                    }}
                  >
                    {analyzing ? "Analyzing..." : "Analyze Video"}
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
          {uploadedVideo && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  height: "100%",
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      mb: 2,
                    }}
                  >
                    Technique Analysis Results
                  </Typography>
                </Box>
                {!analysisComplete && (
                  <Typography color="text.secondary">
                    {analyzing
                      ? "Analyzing your video..."
                      : "Click Analyze Video to start the analysis"}
                  </Typography>
                )}
                {analysisError && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    Error: {analysisError}
                  </Typography>
                )}
                {/* Analysis results section */}
                {analysisComplete && analysisResults && (
                  <>
                    {analysisResults.isNotFallResponseVideo ? (
                      /* For non-choking videos, only show the simple message */
                      <Box
                        sx={{
                          mt: 4,
                          mb: 3,
                          p: 3,
                          border: "1px solid #f44336",
                          borderRadius: 2,
                          backgroundColor: "#ffebee",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="error"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <WarningIcon sx={{ mr: 1 }} />
                          This is not a video relevant for choking management
                        </Typography>
                      </Box>
                    ) : (
                      /* Only show performance evaluation for valid choking videos */
                      <>
                        <Typography
                          variant="h5"
                          color="primary"
                          sx={{ mt: 4, mb: 3, fontWeight: 600 }}
                        >
                          Performance Evaluation
                        </Typography>

                        <Box
                          sx={{
                            mb: 3,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 3,
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            backgroundColor: "#f5f7fa",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: "primary.main",
                              color: "white",
                              borderRadius: "50%",
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "1.5rem",
                              fontWeight: "bold",
                            }}
                          >
                            {analysisResults?.score !== undefined
                              ? analysisResults.score
                              : 9}
                            /10
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              color="primary"
                              gutterBottom
                            >
                              Overall Score
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Based on safety aspects and technique evaluation
                            </Typography>
                          </Box>
                        </Box>

                        {/* Detailed Analysis Table */}
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          Detailed Analysis
                        </Typography>

                        <TableContainer sx={{ height: "100%", mb: 4 }}>
                          <Table
                            size="small"
                            sx={{
                              "& th": {
                                fontWeight: 600,
                                backgroundColor: "#f5f7fa",
                              },
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  Safety Aspect
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  Status
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  Observations
                                </TableCell>
                                <TableCell sx={{ fontSize: "0.875rem" }}>
                                  Recommendations
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analyzing ? (
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    align="center"
                                    sx={{ py: 4 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 2,
                                      }}
                                    >
                                      <CircularProgress />
                                      <Typography>
                                        Analyzing your video... This may take up
                                        to 20 seconds.
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                analysisResults?.results?.map((result) => (
                                  <TableRow
                                    key={result["Safety Aspect"]}
                                    sx={{
                                      "&:nth-of-type(odd)": {
                                        backgroundColor: "#fafbfc",
                                      },
                                      "&:hover": {
                                        backgroundColor: "#f5f7fa",
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 500 }}>
                                      {result["Safety Aspect"]}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "1.25rem",
                                          color:
                                            result["Status"] === "✅"
                                              ? "#2e7d32"
                                              : result["Status"] === "❌"
                                              ? "#d32f2f"
                                              : "#757575",
                                          textShadow:
                                            "0 1px 1px rgba(0,0,0,0.1)",
                                        }}
                                      >
                                        {result["Status"]}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.75rem",
                                          color: "text.secondary",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {result["Observations"]}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.75rem",
                                          color: "text.secondary",
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {result["Recommendations"]}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Feedback Section - Now after the table */}
                        <Box
                          sx={{
                            mb: 3,
                            p: 3,
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            backgroundColor: "#f5f7fa",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{
                              mb: 2,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <FeedbackIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                            Feedback
                          </Typography>

                          {/* Hardcoded feedback that will always display */}
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: "text.primary",
                                lineHeight: 1.6,
                                mb: 2,
                              }}
                            >
                              Your performance in handling this fall situation
                              showed both strengths and areas for development.
                              You demonstrated good initial techniques, though
                              some important protocol steps could be improved.
                            </Typography>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                mt: 2,
                                mb: 1,
                                color: "#1976d2",
                              }}
                            >
                              What You Did Well:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Initial response:
                                </Box>{" "}
                                You quickly approached the patient and
                                established verbal contact
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Basic communication:
                                </Box>{" "}
                                You spoke clearly and at an appropriate volume
                                for a patient with memory issues
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Comfort measures:
                                </Box>{" "}
                                You appropriately reassured the patient
                                throughout the interaction
                              </Typography>
                            </Box>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                mt: 2,
                                mb: 1,
                                color: "#d32f2f",
                              }}
                            >
                              Actions to Include Next Time:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5, color: "text.primary" }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Neurological assessment:
                                </Box>{" "}
                                Remember to perform a basic neurological check
                                when a patient has dizziness and memory loss
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5, color: "text.primary" }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Vital signs:
                                </Box>{" "}
                                Include checking blood pressure, pulse, and
                                respiration, especially for a patient with
                                dizziness
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5, color: "text.primary" }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Environmental assessment:
                                </Box>{" "}
                                Take time to identify potential hazards that may
                                have contributed to the fall
                              </Typography>
                            </Box>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                mt: 2,
                                mb: 1,
                                color: "#ed6c02",
                              }}
                            >
                              Suggestions for Improvement:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Memory loss management:
                                </Box>{" "}
                                Try using simpler, one-step directions and
                                visual cues to better accommodate cognitive
                                limitations
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Dizziness protocol:
                                </Box>{" "}
                                Consider a slower approach when changing the
                                patient's position to prevent worsening vertigo
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Body mechanics:
                                </Box>{" "}
                                Practice bending at the knees and maintaining a
                                straight back when assisting patients
                              </Typography>
                              <Typography
                                component="li"
                                variant="body1"
                                sx={{ mb: 0.5 }}
                              >
                                <Box
                                  component="span"
                                  sx={{ fontWeight: "medium" }}
                                >
                                  Documentation:
                                </Box>{" "}
                                Include more details about the patient's
                                condition before, during, and after the fall in
                                your reports
                              </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mt: 2 }}>
                              You're on the right track with many aspects of
                              fall management. With practice and attention to
                              these suggestions, your skills will continue to
                              improve.
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Video Evaluation 2 Section */}
      <TabPanel value={activeSection} index="video2">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: "100%",
                backgroundColor: "#ffffff",
                borderRadius: 2,
                boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 2,
                  }}
                >
                  Upload Chocking Management Video
                </Typography>

                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{ mb: 2, mt: 3 }}
                >
                  Caretaker Response Mock Drill
                </Typography>

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  <strong>Scenario:</strong>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {scenarioText}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: "bold" }}>
                Mock the drill based on the scenario and then record or upload a
                video of your choking management protocol for comprehensive
                analysis and detailed feedback.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <strong>Instructions:</strong>
                <ul
                  style={{
                    paddingLeft: "20px",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <li>
                    Choose either the "Record Video" or "Upload Video" option
                    below
                  </li>
                  <li>
                    For recording: Click "Start Recording", perform the care
                    technique, then click "Stop Recording"
                  </li>
                  <li>
                    For uploading: Select a video file (MP4 or MOV format, max
                    15MB)
                  </li>
                  <li>
                    Click "Analyze Video" to receive detailed feedback on your
                    technique
                  </li>
                  <li>
                    Ensure good lighting and clear visibility of all movements
                    during the demonstration
                  </li>
                </ul>
              </Typography>

              {/* Video Preview */}
              {(stream || recordedVideo) && (
                <Box sx={{ mb: 3, position: "relative" }}>
                  <video
                    ref={videoRef}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      backgroundColor: "#000",
                    }}
                    autoPlay
                    muted
                    playsInline
                  />
                </Box>
              )}

              {/* Upload and Record Options */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* File Upload Option */}
                <Grid item xs={12} sm={6}>
                  <Box
                    component="label"
                    htmlFor="video-upload-2"
                    sx={{
                      border: "2px dashed",
                      borderColor: "secondary.main",
                      borderRadius: 2,
                      p: 3,
                      height: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <input
                      type="file"
                      id="video-upload-2"
                      accept="video/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Check if file size exceeds 15MB (15 * 1024 * 1024 bytes)
                          if (file.size > 15 * 1024 * 1024) {
                            setAnalysisError(
                              "File size exceeds 15MB limit. Please upload a smaller video file."
                            );
                            e.target.value = null; // Reset the input
                            return;
                          }

                          if (stream) {
                            stream.getTracks().forEach((track) => track.stop());
                            setStream(null);
                          }
                          setUploadedVideo(file);
                          setRecordedVideo(null);
                          setAnalysisComplete(false);
                          setAnalysisError(null); // Clear any previous errors
                        }
                      }}
                    />
                    <UploadIcon
                      sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
                    />
                    <Typography color="secondary.main">Upload Video</Typography>
                    <Typography
                      variant="body2"
                      color="error.main"
                      fontWeight="500"
                    >
                      MP4, MOV (max 15MB)
                    </Typography>
                  </Box>
                </Grid>

                {/* Camera Recording Option */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      border: "2px dashed",
                      borderColor: "secondary.main",
                      borderRadius: 2,
                      p: 3,
                      height: "100%",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={async () => {
                      if (isRecording) {
                        // Stop recording
                        mediaRecorderRef.current?.stop();
                        setIsRecording(false);
                      } else if (stream) {
                        // Start recording
                        chunksRef.current = [];
                        const mediaRecorder = new MediaRecorder(stream);
                        mediaRecorderRef.current = mediaRecorder;
                        mediaRecorder.ondataavailable = (e) => {
                          if (e.data.size > 0) {
                            chunksRef.current.push(e.data);
                          }
                        };
                        mediaRecorder.onstop = () => {
                          const blob = new Blob(chunksRef.current, {
                            type: "video/mp4",
                          });
                          const videoFile = new File(
                            [blob],
                            "recorded-video.mp4",
                            { type: "video/mp4" }
                          );
                          setRecordedVideo(URL.createObjectURL(blob));
                          setUploadedVideo(videoFile);
                          setAnalysisComplete(false);
                          stream.getTracks().forEach((track) => track.stop());
                          setStream(null);
                        };
                        mediaRecorder.start();
                        setIsRecording(true);
                      } else {
                        // Start camera
                        try {
                          const mediaStream =
                            await navigator.mediaDevices.getUserMedia({
                              video: true,
                            });
                          setStream(mediaStream);
                          if (videoRef.current) {
                            videoRef.current.srcObject = mediaStream;
                          }
                          setUploadedVideo(null);
                          setRecordedVideo(null);
                        } catch (err) {
                          console.error("Error accessing camera:", err);
                        }
                      }
                    }}
                  >
                    {isRecording ? (
                      <>
                        <StopIcon
                          sx={{ fontSize: 40, color: "error.main", mb: 1 }}
                        />
                        <Typography color="error.main">
                          Stop Recording
                        </Typography>
                      </>
                    ) : (
                      <>
                        <VideocamIcon
                          sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
                        />
                        <Typography color="secondary.main">
                          {stream ? "Start Recording" : "Use Camera"}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {uploadedVideo && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Selected video: {uploadedVideo.name}
                  </Typography>
                  {/* Video Preview */}
                  <Box
                    sx={{ mt: 2, mb: 2, width: "100%", position: "relative" }}
                  >
                    <video
                      controls
                      width="100%"
                      height="auto"
                      src={
                        recordedVideo ||
                        (uploadedVideo
                          ? URL.createObjectURL(uploadedVideo)
                          : "")
                      }
                      style={{ borderRadius: "8px" }}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    color="secondary"
                    disabled={analyzing}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                    onClick={async () => {
                      if (!uploadedVideo) return;

                      setAnalyzing(true);
                      setAnalysisError(null);

                      try {
                        // Get the protocol steps to evaluate against
                        const protocolToEvaluate = protocolSteps.map(
                          (step) => ({
                            title: step.title,
                            description: step.description,
                          })
                        );

                        try {
                          // First try using the real Gemini API for analysis
                          // Pass the scenarioText to the API for context
                          const results = await analyzeVideoWithGemini(
                            uploadedVideo,
                            protocolToEvaluate,
                            scenarioText
                          );
                          setAnalysisResults(results);
                          setIsAiGenerated(true); // Mark as AI-generated
                          // Show evaluation completed notification
                          setShowEvaluationNotification(true);
                          // Hide notification after 10 seconds
                          setTimeout(
                            () => setShowEvaluationNotification(false),
                            10000
                          );
                        } catch (apiError) {
                          console.error("Gemini API error:", apiError);
                          // If the API fails, fall back to the mock function
                          console.log("Falling back to mock analysis...");
                          const mockResults = await mockAnalyzeVideo(
                            uploadedVideo,
                            protocolToEvaluate,
                            scenarioText
                          );
                          setAnalysisResults(mockResults);
                          setIsAiGenerated(false); // Mark as mock data
                          // Show evaluation completed notification
                          setShowEvaluationNotification(true);
                          // Hide notification after 10 seconds
                          setTimeout(
                            () => setShowEvaluationNotification(false),
                            10000
                          );
                          // Show a warning that we're using mock data
                          setAnalysisError(
                            "Note: Using simulated AI analysis due to API limitations. Results are for demonstration purposes only."
                          );
                        }
                      } catch (error) {
                        console.error("Error analyzing video:", error);
                        setAnalysisError(
                          "Failed to analyze video. Please try again."
                        );
                      } finally {
                        setAnalyzing(false);
                      }
                    }}
                  >
                    {analyzing ? (
                      <>
                        <CircularProgress
                          size={24}
                          sx={{ mr: 1, color: "white" }}
                        />
                        Analyzing Video...
                      </>
                    ) : (
                      "Analyze Video"
                    )}
                  </Button>
                </Box>
              )}

              {/* Space for future features */}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Analysis Results Section */}
            {analysisResults && showEvaluationNotification && (
              <Paper
                elevation={4}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 2,
                  border: "2px solid #2196f3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon
                  sx={{ color: "#2196f3", mr: 1, fontSize: "24px" }}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#2196f3" }}
                >
                  Video evaluation completed!
                </Typography>
              </Paper>
            )}
            {analysisResults && (
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  mb: 3,
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "secondary.main", mr: 2 }}
                    >
                      AI Analysis Results
                    </Typography>
                    <Chip
                      label={isAiGenerated ? "AI Generated" : "Simulated Data"}
                      color={isAiGenerated ? "success" : "warning"}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    color="secondary.main"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Performance Evaluation
                  </Typography>

                  {/* Check if it's a non-fall video */}
                  {analysisResults.isNotFallResponseVideo ? (
                    <Box
                      sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: "#ffebee",
                        border: "1px solid #ef5350",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#d32f2f",
                          fontWeight: 600,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <WarningIcon sx={{ mr: 1 }} />
                        Not a Choking Response Video
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        The submitted video does not appear to demonstrate a
                        choking response scenario. Please ensure your video
                        shows a caregiver responding to a choking response
                        situation according to the scenario provided.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 2,
                          bgcolor: "rgba(255,255,255,0.7)",
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: "50%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#d32f2f",
                            color: "white",
                            mr: 3,
                            boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            0
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
                          >
                            SCORE
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Your video has received a score of 0 as it does not
                            demonstrate the required choking response protocol.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    /* Professional score card for valid fall videos */
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: "#f8f9fa",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      {/* Score circle */}
                      {(() => {
                        const score = Math.round(
                          (analysisResults?.score || 85) / 10
                        );
                        let evaluationText = "";
                        let evaluationColor = "";
                        let bgColor = "";

                        if (score >= 9) {
                          evaluationText = "Excellent";
                          evaluationColor = "#2e7d32";
                          bgColor = "rgba(46, 125, 50, 0.1)";
                        } else if (score >= 6) {
                          evaluationText = "Good";
                          evaluationColor = "#ed6c02";
                          bgColor = "rgba(237, 108, 2, 0.1)";
                        } else {
                          evaluationText = "Needs Improvement";
                          evaluationColor = "#d32f2f";
                          bgColor = "rgba(211, 47, 47, 0.1)";
                        }

                        return (
                          <>
                            <Box
                              sx={{
                                width: 90,
                                height: 90,
                                borderRadius: "50%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: evaluationColor,
                                color: "white",
                                mr: 3,
                                boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                              }}
                            >
                              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                {score}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.65rem",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                OUT OF 10
                              </Typography>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: evaluationColor,
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {evaluationText}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                              >
                                {score >= 9
                                  ? "Outstanding performance with excellent safety protocols."
                                  : score >= 6
                                  ? "Solid performance with good adherence to safety protocols."
                                  : "Some key areas need improvement to ensure patient safety."}
                              </Typography>
                              <Box
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: "rgba(0,0,0,0.08)",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: `${score * 10}%`,
                                    bgcolor: evaluationColor,
                                    borderRadius: 3,
                                  }}
                                />
                              </Box>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>

                {!analysisResults.isNotFallResponseVideo && (
                  <>
                    <Typography
                      variant="h6"
                      color="secondary.main"
                      sx={{ mt: 4, mb: 3, fontWeight: 600 }}
                    >
                      Detailed Analysis
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        mb: 3,
                      }}
                    >
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: "#f8f9fa",
                                "& th": {
                                  fontWeight: 600,
                                  color: "secondary.main",
                                  borderBottom: "2px solid",
                                  borderBottomColor: "secondary.main",
                                  py: 2,
                                },
                              }}
                            >
                              <TableCell width="20%">Safety Aspect</TableCell>
                              <TableCell width="10%" align="center">
                                Status
                              </TableCell>
                              <TableCell width="35%">Observations</TableCell>
                              <TableCell width="35%">Recommendations</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analysisResults.results.map((result, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor: "#f8f9fa",
                                  },
                                  "&:hover": { backgroundColor: "#f0f7ff" },
                                  "& td": { py: 2 },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>
                                  {result["Safety Aspect"]
                                    .replace(/\*\*/g, "")
                                    .replace(/\d+\.\s+/, "")}
                                </TableCell>
                                <TableCell align="center">
                                  {result["Status"] === "✅" ? (
                                    <Chip
                                      label="Good"
                                      size="small"
                                      color="success"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  ) : result["Status"] === "⚠️" ? (
                                    <Chip
                                      label="Warning"
                                      size="small"
                                      color="warning"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Alert"
                                      size="small"
                                      color="error"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ color: "text.secondary" }}>
                                  {result["Observations"]}
                                </TableCell>
                                <TableCell sx={{ color: "text.secondary" }}>
                                  {result["Recommendations"]}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Feedback section for valid fall videos */}
                    <Box
                      sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: "#f5f7fa",
                        border: "1px solid #e0e0e0",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="secondary.main"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FeedbackIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                        Feedback
                      </Typography>

                      {/* Hardcoded feedback that will always display */}
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(255,255,255,0.7)",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ color: "text.primary", lineHeight: 1.6, mb: 2 }}
                        >
                          Your performance in handling this fall situation
                          showed both strengths and areas for development. There
                          are several additional steps that would enhance your
                          care approach.
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            mt: 2,
                            mb: 1,
                            color: "#1976d2",
                          }}
                        >
                          What You Did Well:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Initial approach:
                            </Box>{" "}
                            You established contact with the patient
                            appropriately and assessed consciousness
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Communication style:
                            </Box>{" "}
                            You used clear, simple language appropriate for the
                            patient's condition
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Condition awareness:
                            </Box>{" "}
                            You recognized the patient's{" "}
                            {scenarioText.includes("arthritis")
                              ? "arthritis limitations"
                              : scenarioText.includes("Parkinson")
                              ? "Parkinson's symptoms"
                              : scenarioText.includes("dementia")
                              ? "cognitive challenges"
                              : scenarioText.includes("stroke")
                              ? "post-stroke deficits"
                              : scenarioText.includes("osteoporosis")
                              ? "bone fragility"
                              : scenarioText.includes("diabetes")
                              ? "diabetic complications"
                              : scenarioText.includes("cognitive")
                              ? "memory difficulties"
                              : scenarioText.includes("COPD")
                              ? "breathing difficulties"
                              : "underlying condition"}{" "}
                            and adjusted your approach
                          </Typography>
                        </Box>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            mt: 2,
                            mb: 1,
                            color: "#d32f2f",
                          }}
                        >
                          Important Steps to Include:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5, color: "text.primary" }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Vital signs assessment:
                            </Box>{" "}
                            Remember to check vital signs before moving a
                            patient who{" "}
                            {scenarioText.match(/patient is ([^.]+)/)?.[1] ||
                              "has fallen"}
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5, color: "text.primary" }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              {scenarioText.includes("arthritis")
                                ? "Joint protection"
                                : scenarioText.includes("Parkinson")
                                ? "Medication timing check"
                                : scenarioText.includes("dementia")
                                ? "Orientation protocol"
                                : scenarioText.includes("stroke")
                                ? "Affected side assessment"
                                : scenarioText.includes("osteoporosis")
                                ? "Fracture assessment"
                                : scenarioText.includes("diabetes")
                                ? "Blood glucose check"
                                : scenarioText.includes("cognitive")
                                ? "Memory assessment"
                                : scenarioText.includes("COPD")
                                ? "Oxygen saturation check"
                                : "Condition-specific protocol"}
                              :
                            </Box>{" "}
                            Consider including this check as part of your
                            assessment for this patient's specific condition
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5, color: "text.primary" }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Fall cause investigation:
                            </Box>{" "}
                            Try to investigate what caused the fall while{" "}
                            {scenarioText.match(/fallen while ([^.]+)/)?.[1] ||
                              "performing an activity"}
                            , which helps with prevention
                          </Typography>
                        </Box>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            mt: 2,
                            mb: 1,
                            color: "#ed6c02",
                          }}
                        >
                          Suggestions for Enhancement:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Condition-specific care:
                            </Box>{" "}
                            {scenarioText.includes("arthritis")
                              ? "Consider using gentler support techniques and asking about pain levels when handling arthritic joints"
                              : scenarioText.includes("Parkinson")
                              ? "Allow more time for movement with Parkinson's patients to reduce stress and improve coordination"
                              : scenarioText.includes("dementia")
                              ? "Try using simpler, one-step directions with visual cues for patients with dementia"
                              : scenarioText.includes("stroke")
                              ? "Consider providing additional support to the potentially weaker side for post-stroke patients"
                              : scenarioText.includes("osteoporosis")
                              ? "Use extra caution and proper body mechanics when assisting patients with osteoporosis"
                              : scenarioText.includes("diabetes")
                              ? "Include checking for foot injuries that might not be felt due to neuropathy"
                              : scenarioText.includes("cognitive")
                              ? "Incorporate more orientation cues for patients with cognitive impairment"
                              : scenarioText.includes("COPD")
                              ? "Monitor breathing during exertion and offer rest periods for COPD patients"
                              : "Consider tailoring your approach more specifically to the patient's condition"}
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Environmental assessment:
                            </Box>{" "}
                            Take time to look for potential hazards in the
                            environment that might have contributed to the fall
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Follow-up plan:
                            </Box>{" "}
                            Try to develop more specific, actionable prevention
                            recommendations
                          </Typography>
                          <Typography
                            component="li"
                            variant="body1"
                            sx={{ mb: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: "medium" }}>
                              Documentation:
                            </Box>{" "}
                            Include more details about the patient's condition,
                            fall circumstances, and interventions in your
                            reports
                          </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ mt: 2 }}>
                          You've shown good foundational skills in fall
                          management. With practice and attention to these
                          additional elements, you'll continue to develop your
                          expertise. Review the detailed analysis above for more
                          specific guidance.
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Special feedback section for non-fall videos */}
                {analysisResults.isNotFallResponseVideo && (
                  <Box
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: "#f5f7fa",
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="secondary.main"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FeedbackIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                      Feedback
                    </Typography>

                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(255,255,255,0.7)",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "text.primary", lineHeight: 1.6, mb: 2 }}
                      >
                        <strong>
                          This video does not appear to demonstrate a choking
                          management scenario.
                        </strong>{" "}
                        To properly evaluate your choking management skills,
                        please submit a video that shows you responding to the
                        specific choking scenario provided:
                      </Typography>

                      <Box
                        sx={{
                          p: 2,
                          my: 2,
                          bgcolor: "rgba(25, 118, 210, 0.08)",
                          borderRadius: 1,
                          border: "1px dashed #1976d2",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontStyle: "italic" }}
                        >
                          "{scenarioText}"
                        </Typography>
                      </Box>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          mt: 3,
                          mb: 1,
                          color: "#1976d2",
                        }}
                      >
                        What Your Video Should Include:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                        <Typography
                          component="li"
                          variant="body1"
                          sx={{ mb: 0.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: "medium" }}>
                            Clear demonstration
                          </Box>{" "}
                          of responding to a choking situation based on the
                          scenario
                        </Typography>
                        <Typography
                          component="li"
                          variant="body1"
                          sx={{ mb: 0.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: "medium" }}>
                            Proper assessment techniques
                          </Box>{" "}
                          to determine airway obstruction severity
                        </Typography>
                        <Typography
                          component="li"
                          variant="body1"
                          sx={{ mb: 0.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: "medium" }}>
                            Appropriate intervention methods
                          </Box>{" "}
                          such as abdominal thrusts or chest thrusts
                        </Typography>
                        <Typography
                          component="li"
                          variant="body1"
                          sx={{ mb: 0.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: "medium" }}>
                            Condition-specific considerations
                          </Box>{" "}
                          for the patient's underlying health issues
                        </Typography>
                      </Box>

                      <Typography variant="body1" sx={{ mt: 3 }}>
                        Please record a new video showing your response to the
                        choking scenario. This will allow us to properly
                        evaluate your skills and provide meaningful feedback on
                        your choking management techniques.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {analysisError && (
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  mb: 3,
                  backgroundColor: "#ffebee",
                  borderRadius: 2,
                }}
              >
                <Typography color="error" variant="h6">
                  <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Error
                </Typography>
                <Typography color="error.dark" sx={{ mt: 1 }}>
                  {analysisError}
                </Typography>
              </Paper>
            )}

            {/* Empty space for layout balance */}
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
}

export default ChokingManagementCourse;
